/* eslint-disable max-lines */
import type { LogoutType } from '@luna/utils-ts';
import { createSingleton, mergeHeaders } from '@luna/utils';
import { createQueryString } from '@luna/router';
import { logger } from '@luna/logger';

import { HTTP_STATUS_CODE } from '../HttpStatusCode';
import { tokenRefreshQueue } from './TokenRefreshQueue';
import { DEFAULT_REFRESHABLE_STATUS_CODES, extendedFetch } from './fetchService';
import type { TokenRefresh } from './types';

type Token = string | (() => string);
type LogoutRequestIfo = {
    url: string;
};

export type LogoutHandler = (logoutType: LogoutType, requestInfo: LogoutRequestIfo) => void;
export type InitCustomFetchOptions = {
    logout: LogoutHandler;
    appendHeaders?: (url: string) => Record<string, string>;
    mutateUrl: (url: string) => string;
    onError: (error: ResponseError) => void;
    authToken: Token;
    refreshToken?: TokenRefresh;
    /**
     * Response statuses that should trigger a token refresh attempt followed by a single
     * retry of the request. If the retried request still fails with one of these statuses,
     * logout is triggered with a `http<status>` reason (e.g. 401 -> 'http401', 403 -> 'http403').
     */
    refreshableStatusCodes?: number[];
    includeCredentials?: boolean;
};

type CustomHeaders = Record<string, string | null>;
export type CustomFetchOptions = {
    /** A string to set request's method. */
    method?: string;
    /** An AbortSignal to set request's signal. */
    signal?: AbortSignal | null;
    /**
     * [Experimental]
     *
     * Feature that is not supported in all browsers. Allow setting priority of the request, so it can be postponed.
     * https://developer.mozilla.org/en-US/docs/Web/API/Request/priority
     */
    priority?: 'low' | 'high' | 'auto';
    credentials?: 'include' | 'omit' | 'same-origin';
    skipAuthHeader?: boolean;
    /**
     * Skips the parsing of the response into a JSON, and
     * return the whole response object instead containing
     * also the headers, and status.
     * */
    skipParseJSON?: boolean;
    /**
     * Body override, so we can serialize to JSON plain objects later on.
     *
     * Body will be serialized automatically to proper type if:
     *  - no Content-Type is specified (json is default)
     *  - Content-Type is set to application/json or custom schema (application/custom.schema+json)
     *  - Content-Type is set to application/x-www-form-urlencoded
     *
     *  If the body is not an object, serialization will be skipped.
     */
    body?: BodyInit | Record<string, string> | null | Partial<unknown> | undefined;
    /**
     * Headers override that allows setting header to "null".
     * This is useful in cases, where we need to determine the header by browser - for example in case of uploading a file, where
     * browser needs to set the multipart/form-body header along with the boundary.
     */
    headers?: CustomHeaders;
    redirect?: RequestRedirect;
    onResponse?: (response: Response) => void;
    keepalive?: boolean;
};

function cleanupHeaders(headers: CustomHeaders): Record<string, string> {
    return Object.entries(headers)
        .filter(([, value]) => !!value)
        .reduce(
            (acc, [key, value]) => {
                if (key === 'content-type' && value === 'multipart/form-data') {
                    return acc;
                }

                acc[key] = value as string;
                return acc;
            },
            {} as Record<string, string>,
        );
}

const config: InitCustomFetchOptions = {
    logout: (() => {}) as InitCustomFetchOptions['logout'],
    appendHeaders: (() => ({})) as InitCustomFetchOptions['appendHeaders'],
    mutateUrl: (url: string) => url,
    onError: (() => {}) as InitCustomFetchOptions['onError'],
    authToken: '' as Token,
};

/**
 * Remember previous token, so we don't encounter race-conditions when token is cleared before redirect,
 * but we read anyway it for last request running on background, which could cause race-condition.
 *
 * Example:
 *  - config.authToken reads the token from sessionStorage
 *  - we clear sessionStorage and redirect outside the application
 *  - some pending request is initiated but config.authToken returns empty string because it reads the token from sessionStorage
 *  - error is thrown (empty token) and logout is initiated
 *  - user can end up on a logout page (config.logout logic), instead of the redirection where he was supposed to end up
 */
const { get: getPreviousToken, set: setPreviousToken } = createSingleton('');

/** allows the different application shells to customize the request() function */
export function initRequest({
    logout,
    authToken,
    appendHeaders,
    mutateUrl,
    onError,
    includeCredentials,
    refreshToken,
    refreshableStatusCodes,
}: InitCustomFetchOptions): void {
    config.logout = logout;
    config.authToken = authToken;
    config.appendHeaders = appendHeaders;
    config.mutateUrl = mutateUrl;
    config.onError = onError;
    config.includeCredentials = includeCredentials;
    config.refreshToken = refreshToken;
    config.refreshableStatusCodes = refreshableStatusCodes;
    setPreviousToken('');
}

function getToken(): string {
    const token: string =
        typeof config.authToken === 'function' ? config.authToken() : config.authToken;
    if (!token) {
        return getPreviousToken();
    }
    return token;
}

export function getMutatedUrl(url: string): string {
    return config?.mutateUrl(url) || url;
}

function getResponseError(e: unknown, url: string): ResponseError {
    if (isResponseError(e)) {
        return e;
    }

    return {
        status: 0,
        statusText: e instanceof Error ? e.message : 'Unknown error',
        requestId: '',
        url,
    };
}

/**
 * request overload which doesn't return the parsed JSON.
 * Pass skipParseJSON: true when having to access the request
 * response, such as: headers and status.
 *
 * @throws ResponseError on api error
 * */
export async function request(
    url: string,
    options: { skipParseJSON: true } & CustomFetchOptions,
): Promise<Response>;
export async function request<ServerResponse>(
    url: string,
    options?: CustomFetchOptions,
): Promise<ServerResponse>;

/**
 * Makes a request to the specified URL with optional custom fetch options.
 *
 * @param  url - The URL to which the request will be sent.
 * @param [options] - An object containing any number of custom fetch options.
 * @param [options.method='GET'] - The HTTP method to use for the request (e.g., 'GET', 'POST', 'PUT', etc.).
 * @param [options.signal] - An AbortSignal that can be used to cancel the request.
 * @param [options.priority='auto'] - Sets the priority of the request for better performance (supported in modern browsers only).
 * @param [options.credentials='same-origin'] - Controls whether cookies are included with cross-site requests.
 * @param [options.skipAuthHeader=false] - If true, the authorization header will not be sent in the request.
 * @param [options.skipParseJSON=false] - If true, the response will not be parsed as JSON before being returned.
 * @param [options.body] - The body of the request, which can be a plain object (serialized to JSON), a FormData instance, or null.
 * @param [options.headers] - An object containing custom headers for the request. Values set here will override those calculated by default or provided by other options.
 * @param [options.redirect='follow'] - Specifies how redirects are handled. One of 'follow' (the default), 'error', or 'manual'.
 * @param [options.keepalive] - If true, keeps the request alive even when the page is closed before the request is completed.
 *
 * @example
 * // Using default options
 * request('/api/user');
 *
 * // Sending a POST request with JSON body and custom headers
 * const postData = { name: 'John', email: 'john@example.com' };
 * const response = await request<Response>('https://example.com/api/users', {
 *   method: 'POST',
 *   body: postData,
 *   headers: {
 *     'X-Custom-Header': 'Value'
 *   }
 * });
 *
 * @example
 * // Using the skipAuthHeader option to send a GET request without sending authorization header
 * const data = await request<Response>('/api/protected-resource', { skipAuthHeader: true });
 */
export async function request<ServerResponse>(
    url: string,
    options?: CustomFetchOptions,
): Promise<ServerResponse | Response> {
    await tokenRefreshQueue.waitForTokenRefresh();

    function getFetchOptions(): RequestInit {
        const token = getToken();

        if (token === '' && !options?.skipAuthHeader) {
            config.logout('emptyToken', {
                url,
            });
            throw new Error('no token found');
        }

        setPreviousToken(token);

        const baseHeaders: Record<string, string> = {
            ...(!options?.skipAuthHeader && { Authorization: `Bearer ${token}` }),
            ...config.appendHeaders?.(url),
        };

        const headers = cleanupHeaders(mergeHeaders(baseHeaders, options?.headers));

        return {
            credentials: config.includeCredentials ? 'include' : undefined,
            ...options,
            body: processRequestBody(options, headers),
            headers,
        };
    }

    try {
        const fetchResponse = await extendedFetch({
            url: config.mutateUrl(url),
            getFetchOptions,
            refreshToken: config?.refreshToken,
            refreshableStatusCodes: config?.refreshableStatusCodes,
        });

        if (options?.onResponse) {
            options.onResponse(fetchResponse.clone());
        }

        const response = await checkStatus(url, fetchResponse);

        return options?.skipParseJSON ? response : parseJSON(url, response);
    } catch (e: unknown) {
        const method = options?.method ? `${options.method.toUpperCase()} ` : '';
        const status = isResponseError(e) ? `${e.status} ` : '';
        const msg = `Request failed: ${method}${status}${url}'`;
        logger.warn(msg, e);
        config.onError(getResponseError(e, url));
        return Promise.reject(e);
    }
}

/**
 * Serializable api error.
 */
export type ResponseError<T = unknown> = {
    status: number;
    statusText: string;
    requestId: string;
    url: string;
    responseJSON?: T;
};

/**
 Determines if a given error is a ResponseError thrown usually from request call.
 */
export function isResponseError<T>(value: unknown): value is ResponseError<T> {
    return value instanceof Object && 'status' in value;
}

function processRequestBody(
    options: CustomFetchOptions | undefined,
    headers: Record<string, string>,
): BodyInit | undefined | null {
    const body = options?.body;

    if (!body) {
        return undefined;
    }
    if (typeof body !== 'object') {
        return body;
    }

    const contentType =
        headers?.['Content-Type'] || headers?.['Content-type'] || headers?.['content-type'];

    if (contentType?.match(/^application\/(.*)json(;.+)?$/i)) {
        return JSON.stringify(body);
    }

    if (contentType?.includes('application/x-www-form-urlencoded')) {
        return createQueryString(body as Record<string, string>, {
            withLeadingQuestionMark: false,
        });
    }

    return body as BodyInit | undefined;
}

async function parseJSON(url: string, response: Response): Promise<any | null> {
    if (
        response.status === HTTP_STATUS_CODE.NO_CONTENT ||
        response.status === HTTP_STATUS_CODE.RESET_CONTENT
    ) {
        return null;
    }
    const responseData = await response.text();
    try {
        if (responseData) {
            return JSON.parse(responseData);
        }
        return responseData;
    } catch (e: unknown) {
        logger.warn(`Parsing response failed: ${url}`, e);
        return Promise.reject(e);
    }
}

async function checkStatus(url: string, response: Response): Promise<Response | never> {
    if (
        response.status >= HTTP_STATUS_CODE.OK &&
        response.status < HTTP_STATUS_CODE.MULTIPLE_CHOICES
    ) {
        return response;
    }

    const refreshableStatusCodes =
        config.refreshableStatusCodes ?? DEFAULT_REFRESHABLE_STATUS_CODES;

    if (refreshableStatusCodes.includes(response.status)) {
        config.logout(`http${response.status}` as LogoutType, {
            url,
        });
    }

    const error: ResponseError = {
        status: response.status,
        url: response.url,
        statusText: response.statusText,
        requestId:
            response.headers.get('X-REQUEST-ID') || response.headers.get('EGH-CORRELATIONID') || '',
    };

    try {
        error.responseJSON = await response.clone?.()?.json?.();
    } catch (err) {
        logger.warn('Parsing error failed', err);
    }

    throw error;
}

const defaultOptionsMutationHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
};

/**
 * Shorthand methods for mutation requests
 */
request.post = postRequest;
request.put = <ServerResponse>(url: string, body: any, options: CustomFetchOptions = {}) =>
    request<ServerResponse>(url, {
        ...options,
        headers: mergeHeaders(defaultOptionsMutationHeaders, options?.headers),
        method: 'PUT',
        body,
    });
request.patch = <ServerResponse>(url: string, body: any, options: CustomFetchOptions = {}) =>
    request<ServerResponse>(url, {
        ...options,
        headers: mergeHeaders(defaultOptionsMutationHeaders, options?.headers),
        method: 'PATCH',
        body,
    });
request.delete = <ServerResponse>(url: string, options: CustomFetchOptions = {}) =>
    request<ServerResponse>(url, { ...options, method: 'DELETE' });

request.mutateUrl = (url: string): string => config.mutateUrl(url);

function postRequest<ServerResponse>(
    url: string,
    body?: any,
    options: CustomFetchOptions = {},
): Promise<ServerResponse> {
    return request<ServerResponse>(url, {
        ...options,
        headers: mergeHeaders(defaultOptionsMutationHeaders, options?.headers),
        method: 'POST',
        body,
    });
}

type UploadRequestResult<T> = {
    status: number;
    data: T;
};

class UploadRequestError extends Error {
    constructor(
        public readonly xhr: XMLHttpRequest,
        public readonly event: ProgressEvent,
    ) {
        super();
    }
}

/**
 * Uploads files to a specified URL using XMLHttpRequest.
 * This function provides an upload progress callback and handles token refresh on unauthorized errors.
 *
 * @param options - The configuration options for the upload request.
 * @param [options.signal] - An AbortSignal object to control the cancellation of the request.
 * @param [options.headers] - A mapping of additional headers to send with the request.
 * @param options.url - The URL where to send the files for uploading.
 * @param [options.onProgress] - A function to be called when the upload progress changes.
 * @param options.formData - The FormData object containing the files to be uploaded.
 * @param [options.method='POST'] - The HTTP method to use for the request ('POST' by default).
 * @param [options.skipAuthHeader=false] - Whether to skip sending the authentication header in the request.
 * @param [options.skipParseJSON=false] - Whether to skip parsing the JSON response from the server.
 *
 * @example
 *  import { logger } from '@luna/logger';
 *  import { uploadRequest } from '@luna/api';
 *
 *  // Upload a single file
 *  const formData = new FormData();
 *  formData.append('file', someFile);
 *
 *  const data = await uploadRequest<Response>({
 *    url: '/api/uploads',
 *    method: 'POST',
 *    formData,
 *    onProgress: (event) => logger.log(`Uploading... ${Math.floor(event.loaded / event.total * 100)}%`),
 *  });
 *
 */
export async function uploadRequest<T>({
    signal,
    onProgress,
    formData,
    headers,
    url,
    method = 'POST',
    skipAuthHeader = false,
    skipParseJSON = false,
}: {
    signal?: AbortSignal;
    headers?: Record<string, string | null>;
    onProgress: ((event: ProgressEvent) => void) | undefined;
    formData: FormData;
    url: string;
    method?: string;
    skipAuthHeader?: boolean;
    skipParseJSON?: boolean;
}): Promise<UploadRequestResult<T>> {
    await tokenRefreshQueue.waitForTokenRefresh();

    function getHeaders(): Record<string, string> {
        const token = getToken();

        if (token === '' && !skipAuthHeader) {
            config.logout('emptyToken', {
                url,
            });
            throw new Error('no token found');
        }

        setPreviousToken(token);

        const baseHeaders: Record<string, string | null> = mergeHeaders(
            skipAuthHeader ? undefined : { Authorization: `Bearer ${token}` },
            config.appendHeaders?.(url),
        );

        return cleanupHeaders({
            ...baseHeaders,
            ...headers,
        });
    }

    const upload = async (): Promise<UploadRequestResult<T>> =>
        new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            xhr.open(method, config.mutateUrl(url));

            Object.entries(getHeaders()).forEach(([key, value]) => {
                xhr.setRequestHeader(key, value);
            });

            signal?.addEventListener('abort', () => xhr.abort());

            if (onProgress) {
                xhr.upload.onprogress = onProgress;
            }

            const onLoad = (): void => {
                if (skipParseJSON) {
                    resolve({
                        data: xhr.response,
                        status: xhr.status,
                    });

                    return;
                }

                try {
                    resolve({
                        status: xhr.status,
                        data: JSON.parse(xhr.responseText),
                    });
                } catch {
                    resolve({
                        data: xhr.response,
                        status: xhr.status,
                    });
                }
            };

            const onError = (event: ProgressEvent): void => {
                reject(new UploadRequestError(xhr, event));
            };

            xhr.onload = onLoad;
            xhr.onerror = onError;
            xhr.send(formData);
        });

    try {
        return await upload();
    } catch (error) {
        if (
            error instanceof UploadRequestError &&
            error.xhr.status === HTTP_STATUS_CODE.UNAUTHORIZED &&
            config.refreshToken
        ) {
            return tokenRefreshQueue.refreshToken(config.refreshToken).then(upload);
        }

        throw error;
    }
}
